/**
 * Wolt Restaurant Scraper via Apify
 * Actor: needy_hammock/wolt-restaurant-menu-scraper
 *
 * Input:  restaurantUrl — a Wolt restaurant URL
 * Output: { restaurantName, menu: [{ itemName, price, description, modifiers }] }
 */

const APIFY_TOKEN = import.meta.env.VITE_APIFY_TOKEN;
const ACTOR_ID = 'needy_hammock~wolt-restaurant-menu-scraper';

export async function scrapeWoltRestaurant(woltUrl) {
    if (!APIFY_TOKEN) throw new Error('VITE_APIFY_TOKEN is not configured.');

    const runUrl = `https://api.apify.com/v2/acts/${ACTOR_ID}/run-sync-get-dataset-items?token=${APIFY_TOKEN}&timeout=120&memory=512`;

    const res = await fetch(runUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            restaurantUrl: woltUrl,
            maxItems: 200,
            includeDetails: true,
        }),
    });

    if (!res.ok) {
        const text = await res.text();
        throw new Error(`Apify error ${res.status}: ${text}`);
    }

    const items = await res.json();

    if (!Array.isArray(items) || items.length === 0) {
        throw new Error('הסקריפר לא החזיר נתונים. ודא שהקישור תקין.');
    }

    const venue = items[0];
    const restaurantName = venue.name || extractNameFromUrl(woltUrl);
    const rawMenuItems = venue.menuItems || [];

    if (rawMenuItems.length === 0) {
        // Restaurant exists but is currently offline / outside delivery hours
        throw new Error(
            `המסעדה "${restaurantName || extractNameFromUrl(woltUrl)}" לא פעילה כרגע בוולט. נסה שוב בשעות הפעילות, או הוסף פריטים ידנית.`
        );
    }

    const menu = rawMenuItems.map(normalizeItem).filter(Boolean);
    return { restaurantName, menu };
}

function normalizeItem(raw) {
    // Wolt item name — may be object {he, en} or plain string
    const itemName = extractText(raw.name || raw.title || '');
    if (!itemName) return null;

    // Price in cents (e.g. 4500 = ₪45)
    let price = raw.price ?? raw.baseprice ?? raw.unitPrice ?? 0;
    if (typeof price === 'number' && price >= 100) {
        price = Math.round(price / 100);
    }
    price = Number(price) || 0;

    const description = extractText(raw.description || raw.shortDescription || '');

    const modifiers = parseModifiers(
        raw.options || raw.modifiers || raw.extra_options || []
    );

    return { itemName, price, description, modifiers };
}

function parseModifiers(rawMods) {
    if (!Array.isArray(rawMods) || rawMods.length === 0) return [];

    return rawMods
        .map(mod => ({
            name: extractText(mod.name || mod.title || ''),
            required: mod.required ?? mod.minimum > 0 ?? false,
            multiSelect: (mod.maximum ?? 1) > 1 || mod.type === 'MultipleChoice',
            options: (mod.values || mod.choices || mod.items || [])
                .map(opt => ({
                    label: extractText(opt.name || opt.label || opt.title || ''),
                    price: (() => {
                        let p = opt.price ?? opt.value ?? 0;
                        if (typeof p === 'number' && p >= 100) p = Math.round(p / 100);
                        return Number(p) || 0;
                    })(),
                }))
                .filter(o => o.label),
        }))
        .filter(m => m.name && m.options.length > 0);
}

// Wolt sometimes returns localised strings as { he: '...', en: '...' } objects
function extractText(val) {
    if (!val) return '';
    if (typeof val === 'string') return val.trim();
    if (typeof val === 'object') {
        return (val.he || val.en || val.fi || Object.values(val)[0] || '').trim();
    }
    return String(val).trim();
}

function extractNameFromUrl(url) {
    try {
        const parts = new URL(url).pathname.split('/').filter(Boolean);
        const last = parts[parts.length - 1] || '';
        return last.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    } catch {
        return 'מסעדה';
    }
}
